import React, { useState } from "react";
import { Input } from "@/race-tracker/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/race-tracker/components/ui/select";
import { Button } from "@/race-tracker/components/ui/button";
import { Badge } from "@/race-tracker/components/ui/badge";
import { Textarea } from "@/race-tracker/components/ui/textarea";
import { ExternalLink, Trash2, Check, Pencil } from "lucide-react";

const PARTIES = ["D", "R", "I", "PAC", "Other"];
const partyColors = {
  D: "bg-blue-100 text-blue-700 border-blue-200",
  R: "bg-red-100 text-red-700 border-red-200",
  I: "bg-amber-100 text-amber-700 border-amber-200",
  PAC: "bg-purple-100 text-purple-700 border-purple-200",
  Other: "bg-gray-100 text-gray-700 border-gray-200",
};

export default function OpponentCard({ opponent, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(!opponent.opponent_name || opponent.opponent_name === "New Opponent");
  const [form, setForm] = useState(opponent);

  const save = () => {
    const data = { ...form };
    delete data.id;
    delete data.created_date;
    delete data.updated_date;
    delete data.created_by;
    onUpdate(data);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-card hover:bg-accent/30 transition-colors group">
        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 ${partyColors[opponent.party] || partyColors.Other}`}>
          {opponent.party}
        </Badge>
        <span className="text-xs font-medium flex-1 truncate">{opponent.opponent_name}</span>
        {opponent.ballotpedia_link && (
          <a
            href={opponent.ballotpedia_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary/80 transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setEditing(true)}>
          <Pencil className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="p-3 rounded-lg border border-primary/30 bg-primary/5 space-y-2">
      <div className="grid grid-cols-[1fr_80px] gap-2">
        <Input
          className="h-7 text-xs"
          value={form.opponent_name || ""}
          onChange={(e) => setForm({ ...form, opponent_name: e.target.value })}
          placeholder="Opponent name"
          autoFocus
        />
        <Select value={form.party || "D"} onValueChange={(v) => setForm({ ...form, party: v })}>
          <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PARTIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Input
        className="h-7 text-xs"
        value={form.ballotpedia_link || ""}
        onChange={(e) => setForm({ ...form, ballotpedia_link: e.target.value })}
        placeholder="Ballotpedia URL"
      />
      <Textarea
        className="text-xs min-h-[50px] resize-none"
        value={form.notes || ""}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        placeholder="Notes..."
      />
      <div className="flex justify-between">
        <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive" onClick={onDelete}>
          <Trash2 className="w-3 h-3 mr-1" /> Remove
        </Button>
        <Button size="sm" className="h-6 text-[10px] gap-1" onClick={save}>
          <Check className="w-3 h-3" /> Done
        </Button>
      </div>
    </div>
  );
}